<template>
  <transition name="messenger-slide">
    <div v-if="open" class="messenger-panel">
      <!-- Header -->
      <div class="msg-header">
        <button v-if="view !== 'list'" class="msg-back" type="button" @click="goBack">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>
        <span class="msg-header-title">
          {{ view === 'list' ? 'Mensagens' : view === 'new' ? 'Nova conversa' : activeConvName }}
        </span>
        <button class="msg-close" type="button" @click="$emit('close')">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- CONVERSATION LIST -->
      <div v-if="view === 'list'" class="msg-view">
        <div v-if="messengerStore.state.loading" class="msg-empty">Carregando...</div>
        <div v-else-if="messengerStore.state.conversations.length === 0" class="msg-empty">
          Nenhuma conversa ainda.<br/>Clique em "Nova conversa" para começar.
        </div>
        <div v-else class="conv-list">
          <button
            v-for="conv in messengerStore.state.conversations"
            :key="conv.id"
            class="conv-item"
            type="button"
            @click="openConversation(conv)"
          >
            <div class="conv-avatar">
              <img v-if="conv.otherAvatar && conv.otherAvatar.startsWith('/uploads/')" :src="resolveAvatar(conv.otherAvatar)" alt="avatar" />
              <span v-else class="conv-avatar-initials">{{ initials(conv.otherName) }}</span>
            </div>
            <div class="conv-info">
              <div class="conv-name-row">
                <span class="conv-name">{{ conv.otherName }}</span>
                <span class="conv-time">{{ formatTime(conv.lastMessageAt) }}</span>
              </div>
              <div class="conv-preview-row">
                <span class="conv-preview">
                  <span v-if="conv.lastMessageBy === currentUsername" class="conv-preview-me">Você: </span>
                  {{ conv.lastMessageText || 'Sem mensagens' }}
                </span>
                <span v-if="conv.unreadCount > 0" class="conv-unread">{{ conv.unreadCount > 9 ? '+9' : conv.unreadCount }}</span>
              </div>
            </div>
          </button>
        </div>

        <div class="msg-footer">
          <button class="btn-new-conv" type="button" @click="openNewConv">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            Nova conversa
          </button>
        </div>
      </div>

      <!-- NEW CONVERSATION — user picker -->
      <div v-else-if="view === 'new'" class="msg-view">
        <div class="new-search-wrap">
          <input
            v-model="userSearch"
            class="new-search"
            type="text"
            placeholder="Buscar por nome ou username..."
            autofocus
          />
        </div>
        <div v-if="loadingUsers" class="msg-empty">Carregando usuários...</div>
        <div v-else-if="filteredUsers.length === 0" class="msg-empty">Nenhum usuário encontrado.</div>
        <div v-else class="user-list">
          <button
            v-for="u in filteredUsers"
            :key="u.username"
            class="user-item"
            type="button"
            @click="startConversation(u.username)"
            :disabled="startingConv"
          >
            <div class="conv-avatar">
              <img v-if="u.avatar && u.avatar.startsWith('/uploads/')" :src="resolveAvatar(u.avatar)" alt="avatar" />
              <span v-else class="conv-avatar-initials">{{ initials(u.name) }}</span>
            </div>
            <div class="user-info">
              <span class="user-name">{{ u.name }}</span>
              <span class="user-username">@{{ u.username }}</span>
            </div>
          </button>
        </div>
      </div>

      <!-- CONVERSATION VIEW -->
      <div v-else-if="view === 'chat'" class="msg-view chat-view">
        <div class="chat-messages" ref="chatMessagesEl">
          <div v-if="messengerStore.messagesLoading.value" class="msg-empty">Carregando...</div>
          <div v-else-if="messengerStore.activeMessages.value.length === 0" class="msg-empty">
            Nenhuma mensagem. Diga olá!
          </div>
          <div
            v-for="msg in messengerStore.activeMessages.value"
            :key="msg.id"
            class="chat-bubble-wrap"
            :class="msg.senderUsername === currentUsername ? 'bubble-own' : 'bubble-other'"
          >
            <div class="chat-bubble">{{ msg.text }}</div>
            <span class="bubble-time">{{ formatTime(msg.createdAt) }}</span>
          </div>
        </div>
        <div class="chat-input-wrap">
          <input
            v-model="inputText"
            class="chat-input"
            type="text"
            placeholder="Mensagem..."
            @keydown.enter.prevent="sendMessage"
          />
          <button class="chat-send" type="button" :disabled="!inputText.trim() || sending" @click="sendMessage">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { messengerStore, type ConversationPreview } from '@/store/messenger'
import { apiFetch, getApiBase } from '@/store/api'
import { timezoneStore } from '@/store/timezone'

interface AccountBasic {
  username: string
  name: string
  avatar: string
}

export default defineComponent({
  name: 'MessengerPanel',
  props: {
    open: { type: Boolean, default: false },
    currentUsername: { type: String, default: '' },
    currentSystemId: { type: String, default: '' }
  },
  emits: ['close'],
  setup(props) {
    const view = ref<'list' | 'new' | 'chat'>('list')
    const activeConvId = ref('')
    const activeConvName = ref('')
    const inputText = ref('')
    const sending = ref(false)
    const chatMessagesEl = ref<HTMLElement | null>(null)

    // New conversation
    const userSearch = ref('')
    const allUsers = ref<AccountBasic[]>([])
    const loadingUsers = ref(false)
    const startingConv = ref(false)

    const apiBase = getApiBase()

    const resolveAvatar = (avatar: string) => {
      if (avatar.startsWith('/uploads/')) return `${apiBase}${avatar}`
      return avatar
    }

    const initials = (name: string) =>
      (name || '?').split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()

    const formatTime = (iso: string) => {
      if (!iso) return ''
      try {
        const tz = timezoneStore.effectiveTimeZone.value
        const d = new Date(iso)
        const now = new Date()
        const dayOf = (x: Date) => x.toLocaleDateString('en-CA', { timeZone: tz })
        const isToday = dayOf(d) === dayOf(now)
        if (isToday) return d.toLocaleTimeString('pt-BR', { timeZone: tz, hour: '2-digit', minute: '2-digit' })
        return d.toLocaleDateString('pt-BR', { timeZone: tz, day: '2-digit', month: '2-digit' })
      } catch { return '' }
    }

    const filteredUsers = computed(() => {
      const q = userSearch.value.toLowerCase().trim()
      return allUsers.value.filter(u =>
        u.username.toLowerCase() !== props.currentUsername.toLowerCase() &&
        (!q || u.name.toLowerCase().includes(q) || u.username.toLowerCase().includes(q))
      )
    })

    const scrollToBottom = async () => {
      await nextTick()
      if (chatMessagesEl.value) {
        chatMessagesEl.value.scrollTop = chatMessagesEl.value.scrollHeight
      }
    }

    const openConversation = async (conv: ConversationPreview) => {
      activeConvId.value = conv.id
      activeConvName.value = conv.otherName
      view.value = 'chat'
      await messengerStore.loadMessages(conv.id)
      scrollToBottom()
    }

    const openNewConv = async () => {
      view.value = 'new'
      userSearch.value = ''
      loadingUsers.value = true
      try {
        const accounts = await apiFetch<AccountBasic[]>('/accounts')
        allUsers.value = accounts
      } catch { /* ignore */ } finally {
        loadingUsers.value = false
      }
    }

    const startConversation = async (username: string) => {
      startingConv.value = true
      try {
        const convId = await messengerStore.openConversationWith(username)
        // Reload conversations list
        await messengerStore.loadConversations()
        const conv = messengerStore.state.conversations.find(c => c.id === convId)
        if (conv) {
          await openConversation(conv)
        } else {
          // Create a minimal conv object
          const otherUser = allUsers.value.find(u => u.username === username)
          activeConvId.value = convId
          activeConvName.value = otherUser?.name || username
          view.value = 'chat'
          await messengerStore.loadMessages(convId)
          scrollToBottom()
        }
      } catch { /* ignore */ } finally {
        startingConv.value = false
      }
    }

    const sendMessage = async () => {
      if (!inputText.value.trim() || sending.value) return
      sending.value = true
      const text = inputText.value.trim()
      inputText.value = ''
      try {
        await messengerStore.sendMessage(activeConvId.value, text, props.currentUsername)
        scrollToBottom()
      } catch {
        inputText.value = text // restore on error
      } finally {
        sending.value = false
      }
    }

    const goBack = () => {
      if (view.value === 'chat' || view.value === 'new') {
        view.value = 'list'
        activeConvId.value = ''
        activeConvName.value = ''
        messengerStore.loadConversations()
      }
    }

    // Poll for new messages when chat is open
    let pollTimer: ReturnType<typeof setInterval> | null = null

    watch(() => props.open, (isOpen) => {
      if (isOpen) {
        messengerStore.loadConversations()
        pollTimer = setInterval(async () => {
          if (view.value === 'chat' && activeConvId.value) {
            await messengerStore.pollMessages(activeConvId.value)
            scrollToBottom()
          } else if (view.value === 'list') {
            messengerStore.loadConversations()
          }
        }, 5000)
      } else {
        if (pollTimer) { clearInterval(pollTimer); pollTimer = null }
        view.value = 'list'
        activeConvId.value = ''
        activeConvName.value = ''
      }
    })

    watch(() => messengerStore.activeMessages.value.length, () => {
      scrollToBottom()
    })

    onBeforeUnmount(() => {
      if (pollTimer) clearInterval(pollTimer)
    })

    return {
      messengerStore,
      view, activeConvName, inputText, sending,
      chatMessagesEl, userSearch, allUsers, filteredUsers, loadingUsers, startingConv,
      resolveAvatar, initials, formatTime,
      openConversation, openNewConv, startConversation, sendMessage, goBack
    }
  }
})
</script>

<style scoped lang="scss">
.messenger-panel {
  position: fixed;
  top: var(--navbar-h, 48px);
  right: 0;
  bottom: 0;
  width: 360px;
  background: #fff;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.14);
  display: flex;
  flex-direction: column;
  z-index: 900;
  border-left: 1px solid #e5e7eb;
  font-family: var(--font-body, 'Inter', sans-serif);
}

/* Slide animation */
.messenger-slide-enter-active,
.messenger-slide-leave-active {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.messenger-slide-enter-from,
.messenger-slide-leave-to {
  transform: translateX(100%);
}

/* Header */
.msg-header {
  height: 52px;
  background: var(--system-sidebar, #0a3460);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  flex-shrink: 0;
}

.msg-back {
  background: transparent;
  border: none;
  color: rgba(255,255,255,0.8);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  &:hover { color: #fff; background: rgba(255,255,255,0.1); }
}

.msg-header-title {
  flex: 1;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.msg-close {
  background: transparent;
  border: none;
  color: rgba(255,255,255,0.7);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  &:hover { color: #fff; background: rgba(255,255,255,0.1); }
}

/* View container */
.msg-view {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.msg-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
  padding: 32px;
  line-height: 1.6;
}

/* Conversation list */
.conv-list {
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.conv-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: transparent;
  border: none;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
  &:hover { background: #f9fafb; }
}

.conv-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
}

.conv-avatar-initials {
  font-size: 14px;
  font-weight: 700;
  color: #6b7280;
}

.conv-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.conv-name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.conv-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.conv-time {
  font-size: 11px;
  color: #9ca3af;
  flex-shrink: 0;
}

.conv-preview-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.conv-preview {
  flex: 1;
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.conv-preview-me {
  color: #9ca3af;
  font-style: italic;
}

.conv-unread {
  flex-shrink: 0;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  background: #0f4c81;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
}

/* Footer - new conversation */
.msg-footer {
  flex-shrink: 0;
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.btn-new-conv {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px;
  background: #0f4c81;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #0d3d6a; }
}

/* New conversation - user list */
.new-search-wrap {
  padding: 12px 14px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.new-search {
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  font-family: inherit;
  &:focus { border-color: #0f4c81; }
}

.user-list {
  flex: 1;
  overflow-y: auto;
}

.user-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: transparent;
  border: none;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
  &:hover:not(:disabled) { background: #f0f4f8; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.user-username {
  font-size: 12px;
  color: #6b7280;
}

/* Chat view */
.chat-view {
  display: flex;
  flex-direction: column;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overscroll-behavior: contain;
}

.chat-bubble-wrap {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 82%;

  &.bubble-own {
    align-self: flex-end;
    align-items: flex-end;
  }

  &.bubble-other {
    align-self: flex-start;
    align-items: flex-start;
  }
}

.chat-bubble {
  padding: 9px 13px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.45;
  word-break: break-word;

  .bubble-own & {
    background: #0f4c81;
    color: #fff;
    border-bottom-right-radius: 4px;
  }

  .bubble-other & {
    background: #f3f4f6;
    color: #111827;
    border-bottom-left-radius: 4px;
  }
}

.bubble-time {
  font-size: 10px;
  color: #9ca3af;
}

.chat-input-wrap {
  flex-shrink: 0;
  display: flex;
  gap: 8px;
  padding: 12px 14px;
  border-top: 1px solid #e5e7eb;
  background: #fff;
}

.chat-input {
  flex: 1;
  padding: 9px 12px;
  border: 1.5px solid #d1d5db;
  border-radius: 20px;
  font-size: 14px;
  outline: none;
  font-family: inherit;
  &:focus { border-color: #0f4c81; }
}

.chat-send {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #0f4c81;
  color: #fff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
  &:hover:not(:disabled) { background: #0d3d6a; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}

@media (max-width: 600px) {
  .messenger-panel {
    width: 100vw;
  }
}
</style>
